import { HTML5Video } from '@24i/player-html5';
import {
    EMPTY_TRACK_ID,
    BufferedRanges,
    DRM_TYPE,
    IAudioTrack,
    IMediaInfo,
    ISeekableRange,
    ISourceDrm,
    ITextTrack,
    IDurationChangeEvent,
    IVideoTrack,
    TimeInMs,
    ISupportedFormats,
    IPlayerProperties,
    ErrorCodes,
    SeekType,
    IThumbnail,
    IPerformanceTweaks
} from '@24i/player-base';
import {
    MILLISECONDS_IN_SECOND,
    millisecondsToSeconds,
    secondsToMilliseconds
} from '@24i/player-utils';
import dashjs, {
    MediaInfo,
    ErrorEvent,
    TrackChangeRenderedEvent,
    FragmentRequest,
    QualityChangeRenderedEvent,
    BitrateInfo,
    MediaPlayerClass,
    ProtectionDataSet,
    MediaType,
    MediaPlayerSettingClass,
    MediaPlayer,
    GenericErrorEvent,
    MediaPlayerErrorEvent,
    DownloadErrorEvent,
    ManifestErrorEvent,
    TimedTextErrorEvent
} from 'dashjs';
import { version as playerVersion } from 'dashjs/package.json';
import { mapToAudioTrack, mapToTextTrack, mapToVideoTrack } from '../helpers';
import { DashJsSupportedFormats } from '../support';

export interface IDashPlayerProperties extends IPlayerProperties {
    customConfiguration?: {
        config?: {
            streamController?: any;
            capabilities?: any;
            playbackController?: any;
            mediaPlayerModel?: any;
            abrController?: any;
            mediaController?: any;
            settings?: any;
        };
        settings?: MediaPlayerSettingClass;
    };
}

const BYTES_IN_KB = 1024;

const MediaTypes = {
    VIDEO: 'video',
    AUDIO: 'audio',
    TEXT: 'text',
    IMAGE: 'image'
};

/**
 * Player configuration, you should configure player via customConfiguration in the props
 */
const DEFAULT_SETTINGS: MediaPlayerSettingClass = {
    debug: { logLevel: 0 },
    streaming: {
        abr: {
            useDefaultABRRules: true,
            autoSwitchBitrate: { video: true }
        },
        buffer: {
            fastSwitchEnabled: true
        },
        scheduling: {
            scheduleWhilePaused: false
        }
    }
};

export class DashJsPlayer extends HTML5Video {
    // Player version obtained from the dashjs package version
    static version: string = playerVersion;

    static playerName = 'dashjs';

    static SupportedFormats: ISupportedFormats = new DashJsSupportedFormats();

    readonly supportedFormats = DashJsPlayer.SupportedFormats;

    // initializationConfig: IDashPlayerProperties;

    // mediaPlayer: MediaPlayerClass;

    // metadata: any;

    // lastDuration: TimeInMs;

    /**
     * This value marks the inital start position in millisecond.
     * It is used for normalizing time values of live streams. For instance: currentTime,
     * seekableRange, and bufferedRanges.
     */
    initialStartPositionInMs: number | null = null;

    // For quality selection
    isAutomaticModeEnabled = true;

    /**
     * Returns the engine name
     */
    public getPlayerName(): string {
        return DashJsPlayer.playerName;
    }

    /**
     * Returns the player version
     *
     * @returns string
     * @memberof PlayerBase
     */
    public getVersion(): string {
        return DashJsPlayer.version;
    }

    protected updateSettings(settings: object): void {
        try {
            this.mediaPlayer.updateSettings(settings);
        } catch (error) {
            this.onError(error);
        }
    }

    /**
     * Creates dashjs instance.
     *
     * @returns {Promise<void>}
     */
    initializePlayer() {
        try {
            try {
                this.mediaPlayer = dashjs.MediaPlayer().create();
            } catch (error) {
                throw new Error(error);
            }
            if (!this.mediaPlayer) {
                throw new Error(
                    'DashJs is not supported in this browser. Please try different player instead.'
                );
            }

            this.mediaPlayer.updateSettings({
                debug: {
                    logLevel: 3
                },
                streaming: {
                    timeShiftBuffer: {
                        calcFromSegmentTimeline: true
                    },
                    delay: {
                        liveDelay: 30,
                        liveDelayFragmentCount: NaN
                    },
                    gaps: {
                        jumpGaps: true,
                        jumpLargeGaps: true,
                        enableSeekFix: true,
                        enableStallFix: true,
                        smallGapLimit: 5,
                        threshold: 0.3,
                        stallSeek: 5
                    },
                    text: {
                        defaultEnabled: false
                    },
                    abr: {
                        autoSwitchBitrate: {
                            video: true
                        },
                        bandwidthSafetyFactor: 0.7
                    },
                    utcSynchronization: {
                        enabled: false,
                        backgroundAttempts: 3
                    },
                    fragmentRequestTimeout: 0,
                    retryIntervals: { InitializationSegment: 5000 }
                }
            });
            // this.mediaPlayer.resetSettings();

            // const newSettings = settings;

            // if (this.trackRestrictions) {
            //     const streamingSettings: any = {
            //         abr: {
            //             minBitrate: {
            //                 video: this.trackRestrictions.minBitrate ?? 0,
            //             },
            //             maxBitrate: {
            //                 video: this.trackRestrictions.maxBitrate ?? -1,
            //             },
            //         },
            //     };
            //     newSettings.streaming = {
            //         ...settings.streaming,
            //         ...streamingSettings,
            //     };
            // }

            // this.updateSettings(newSettings);

            // window.dashjs = dashjs;
            // eslint-disable-next-line global-require,import/extensions
            // require('dashjs/dist/dash.mss.min.js');
            super.initializePlayer();
        } catch (e) {
            console.log('error', e);
        }
    }

    /**
     * register all needed dash.js listeners.
     */
    protected bindEventListeners(): void {
        if (!this.mediaPlayer) return;
        this.mediaPlayer.on('error', this.onDashJSErrorEvent);
        this.mediaPlayer.on('trackChangeRendered', this.onDashJSTrackChanged);
        this.mediaPlayer.on('qualityChangeRendered', this.onDashJSQualityChange);
        this.mediaPlayer.on('fragmentLoadingCompleted', this.onDashJSFragmentCompleted);
        this.mediaPlayer.on('urn:google:dai:2018', this.onDashJSMetadata);

        this.mediaElement.addEventListener('timeupdate', this.onDashTimeUpdate);
        super.bindEventListeners();
    }

    /**
     * unregister all dash.js listeners.
     */
    protected unbindEventListeners(): void {
        if (!this.mediaPlayer) return;
        this.mediaPlayer.off('error', this.onDashJSErrorEvent);
        this.mediaPlayer.off('trackChangeRendered', this.onDashJSTrackChanged);
        this.mediaPlayer.off('qualityChangeRendered', this.onDashJSQualityChange);
        this.mediaPlayer.off('fragmentLoadingCompleted', this.onDashJSFragmentCompleted);
        this.mediaPlayer.off('urn:google:dai:2018', this.onDashJSMetadata);
        this.mediaElement.removeEventListener('timeupdate', this.onDashTimeUpdate);
        super.unbindEventListeners();
    }

    /**
     * Loads current source to dashjs.
     * Restarts previous and load new configuration.
     * customConfigurtion more details on the github
     * https://github.com/Dash-Industry-Forum/dash.js/blob/development/src/core/Settings.js
     *
     * @returns {Promise<any>}
     */
    protected async mediaLoad() {
        if (!this.mediaPlayer) return;

        const { url, drm } = this.source;
        const autoPlay = false;
        // const { autoPlay, quality } = this.initializationConfig;

        // if (autoPlay !== undefined) this.autoPlay = autoPlay;
        // if (quality !== undefined) this.quality = quality;
        if (drm) this.setDrmProtection(this.source.drm);
        this.initialStartPositionInMs = null;

        const resolvedStartPosition = this.getStreamTime(
            this.startPosition,
            this.startPositionType
        );
        this.mediaPlayer.initialize(this.mediaElement, null, true);

        if (this.startPositionType === 'utc' && this.startPosition) {
            const starttime = `posix:${this.startPosition / 1000}`;
            this.mediaPlayer.attachSource(url, starttime);
        } else {
            this.mediaPlayer.attachSource(url, this.startPosition / 1000);
        }
        /*
         * In order to support TTML PNG subtitles, DASHJS requires you to provide
         * a PNG rendering container. In this case, we are providing a 100% w/h container
         * with disabled pointer events.
         */
        this.mediaPlayer.attachTTMLRenderingDiv(this.ttmlRenderingContainer);
        // this.mediaPlayer.attachView(this.mediaElement);
        // this.mediaPlayer.attachSource(url);
    }

    public async setDrmProtection(drmConfiguration: ISourceDrm) {
        await super.setDrmProtection(drmConfiguration);
        let protectionData: ProtectionDataSet = {};
        if (
            drmConfiguration.packageName === DRM_TYPE.PLAYREADY ||
            drmConfiguration.packageName === DRM_TYPE.WIDEVINE
        ) {
            const { packageName, licenseServer, httpRequestHeaders } = drmConfiguration;
            protectionData = {
                [packageName]: {
                    serverURL: licenseServer,
                    httpRequestHeaders
                }
            };
        }
        this.mediaPlayer.setProtectionData(protectionData);
    }

    /**
     * Unloads the current video source.
     */
    unload(): void {
        super.unload();
        this.initialStartPositionInMs = null;
        if (this.mediaElement?.mediaKeys && this.mediaElement?.mediaKeys.createSession) {
            /**
             * Without this hack, attachSource sometimes throws an error:
             * DOMException: Failed to execute 'setMediaKeys'
             *  on 'HTMLMediaElement': Another request is in progress.
             * This is a bug in dash.js itself, as they are not handling setMediaKeys promise at all
             * https://github.com/Dash-Industry-Forum/dash.js/
             *  blob/6e7612aff6ab0d6071990fb79e658116025a9495/
             *  src/streaming/protection/models/ProtectionModel_21Jan2015.js#L228
             * https://github.com/Dash-Industry-Forum/dash.js/
             *  blob/6e7612aff6ab0d6071990fb79e658116025a9495/
             *  src/streaming/protection/models/ProtectionModel_21Jan2015.js#L238
             * Maybe we should create PR to dash.js?
             */
            const session = this.mediaElement.mediaKeys.createSession();
            if (!session.closed) {
                session.close();
            }
        }
        if (this.mediaPlayer) {
            let hasSourceLoaded = false;
            try {
                this.mediaPlayer.getSource();
                hasSourceLoaded = true;
            } catch (error) { }
            if (hasSourceLoaded) {
                const protectionControler = this.mediaPlayer.getProtectionController();
                if (protectionControler) protectionControler.stop();
                // this.mediaPlayer.getProtectionController().reset();
                this.mediaPlayer.attachSource(null);
            }
        }
    }

    /*
     * DESTROY
     */

    /**
     * Unloads and Destroys the player.
     */
    destroy(): void {
        super.destroy();
        if (this.mediaPlayer && this.mediaPlayer.destroy) {
            this.mediaPlayer.reset();
            this.mediaPlayer.destroy();
            this.mediaPlayer = null;
        }
    }

    /*
     * DashJS EVENT HANDLERS
     */

    /**
     * OnErrorEvent handler.
     *
     * @param {string} errorEvent - Received DashJs error.
     */
    onDashJSErrorEvent = (errorEvent: ErrorEvent): void => {
        const dashError = errorEvent.error;
        if (typeof dashError === 'object') {
            const mediaPlayerError = errorEvent as MediaPlayerErrorEvent;
            const nativeCodeNumber = mediaPlayerError.error.code;
            const nativeCodeName =
                DashJsPlayer.getErrorCodeName(nativeCodeNumber) || nativeCodeNumber.toString();

            super.onError({
                code: ErrorCodes.PLAYER_FAILURE,
                message: dashError.message,
                engine: DashJsPlayer.playerName,
                engineVersion: DashJsPlayer.version,
                nativeCode: nativeCodeName.toString(),
                source: this.sourceConfigurationObject
            });
        } else if (dashError === 'download') {
            const downloadError = errorEvent as DownloadErrorEvent;
            super.onError({
                code: ErrorCodes.NETWORK_FAILURE,
                message: `Failed to download ${downloadError.event.url}`,
                engine: DashJsPlayer.playerName,
                engineVersion: DashJsPlayer.version,
                nativeCode: dashError,
                source: this.sourceConfigurationObject
            });
        } else if (dashError === 'manifestError') {
            const manifestError = errorEvent as ManifestErrorEvent;
            super.onError({
                code: ErrorCodes.PLAYER_FAILURE,
                message: manifestError.event.message,
                engine: DashJsPlayer.playerName,
                engineVersion: DashJsPlayer.version,
                nativeCode: dashError,
                source: this.sourceConfigurationObject
            });
        } else if (dashError === 'cc') {
            const timedTextError = errorEvent as TimedTextErrorEvent;
            super.onError({
                code: ErrorCodes.PLAYER_FAILURE,
                message: timedTextError.event.message,
                engine: DashJsPlayer.playerName,
                engineVersion: DashJsPlayer.version,
                nativeCode: dashError,
                source: this.sourceConfigurationObject
            });
        } else if (typeof dashError === 'string') {
            const genericError = errorEvent as GenericErrorEvent;
            super.onError({
                code: ErrorCodes.PLAYER_FAILURE,
                message: `Playback failed because of ${genericError.event}`,
                engine: DashJsPlayer.playerName,
                engineVersion: DashJsPlayer.version,
                nativeCode: dashError,
                source: this.sourceConfigurationObject
            });
        }
    };

    // onShakaManifestParsed = () => {
    //     this.manifest = this.shakaPlayer.getManifest();
    //     const presentationTimeline = this.manifest?.presentationTimeline;
    //     if (presentationTimeline) {
    //         this.initialStartPositionInMs = secondsToMilliseconds(
    //             presentationTimeline.getSeekRangeStart()
    //         );
    //         const resolvedStartPosition = this.getStreamTime(
    //             this.startPosition,
    //             this.startPositionType
    //         );
    //         if (Number.isFinite(resolvedStartPosition)) {
    //             const shakaStartTime = millisecondsToSeconds(
    //                 resolvedStartPosition + this.initialStartPositionInMs
    //             );
    //             this.shakaPlayer.updateStartTime(shakaStartTime);
    //         }
    //     }
    // };

    /**
     * OnCanPlay handler
     */
    onCanPlay = () => {
        if (this.initialStartPositionInMs === null) {
            this.initialStartPositionInMs = secondsToMilliseconds(
                this.mediaPlayer.durationAsUTC() - this.mediaPlayer.duration()
            );
        }
        super.onCanPlay();
    };

    onDashJSFragmentCompleted = (data: FragmentRequest) => {
        const { duration } = this;
        if (this.live && data.mediaType === MediaTypes.VIDEO) {
            if (typeof duration !== 'undefined' && this.lastDuration !== duration) {
                this.lastDuration = duration;
                this.triggerEvent('durationchange', <IDurationChangeEvent>{
                    duration
                });
            }
        }
    };

    onDashJSMetadata = (data: any) => {
        const { type, event } = data;
        const { messageData, calculatedPresentationTime } = event;
        this.triggerEvent('metadata', {
            type,
            value: messageData,
            time: calculatedPresentationTime
        });
    };

    onDashJSTrackChanged = (event: TrackChangeRenderedEvent) => {
        if (event.mediaType === MediaTypes.AUDIO) {
            this.triggerEvent('audiotrackchanged', {
                track: this.selectedAudioTrack
            });
        } else if (event.mediaType === MediaTypes.TEXT) {
            this.triggerEvent('texttrackchanged', {
                track: this.selectedTextTrack
            });
        } else if (event.mediaType === MediaTypes.VIDEO) {
            this.triggerEvent('videotrackchanged', {
                track: this.selectedVideoTrack
            });
        }
    };

    onDashJSQualityChange = (event: QualityChangeRenderedEvent) => {
        if (event.mediaType === MediaTypes.VIDEO) {
            const bitrates = this.mediaPlayer.getBitrateInfoListFor('video');
            let newBitrate;
            bitrates.forEach((bitrateInfo: BitrateInfo) => {
                if (bitrateInfo.qualityIndex === event.newQuality) {
                    newBitrate = bitrateInfo.bitrate;
                }
            });

            this.triggerEvent('bitratechange', {
                bitrate: newBitrate
            });
        }
    };

    onDashTimeUpdate = () => {
        this.updateMetrics();
    };

    /*
     * PROGRESS HANDLING
     */

    /**
     * Return true if the displayed video is a live stream.
     *
     * @returns {boolean}
     */
    get live(): boolean {
        if (!this.mediaPlayer?.isReady() || this.adsManager.isPlayingClientSide) return false;
        return this.mediaPlayer.isDynamic();
    }

    /**
     * Gets currentTime of mediaElement in milliseconds.
     *
     * @returns {TimeInMs}
     */
    get currentTime(): TimeInMs {
        if (!this.mediaElement) return 0;
        if (!this.hasLoaded || !this.mediaPlayer?.isReady()) return 0;
        if (this.adsManager.isPlayingClientSide) return this.adsManager.currentTime;
        if (!this.live) {
            return secondsToMilliseconds(this.mediaPlayer.time()) || 0;
        }
        if (this.initialStartPositionInMs === null) return 0;
        return Math.max(
            0,
            secondsToMilliseconds(this.mediaPlayer.timeAsUTC()) - this.initialStartPositionInMs || 0
        );
    }

    /**
     * Gets duration of mediaPlayer in milliseconds.
     *
     * @returns {TimeInMs}
     */
    get duration(): TimeInMs {
        if (!this.hasLoaded || !this.mediaPlayer.isReady()) return 0;
        if (this.adsManager.isPlayingClientSide) return this.adsManager.duration;
        if (this.live) {
            return Infinity;
        }

        const duration =
            this.mediaPlayer?.getDashMetrics()?.getCurrentDVRInfo('video')?.range?.end !==
                undefined &&
                this.mediaPlayer?.getDashMetrics()?.getCurrentDVRInfo('video')?.range?.start !==
                undefined
                ? this.mediaPlayer.getDashMetrics().getCurrentDVRInfo('video').range.end -
                this.mediaPlayer.getDashMetrics().getCurrentDVRInfo('video').range.start
                : 0;

        return secondsToMilliseconds(duration) || 0;
    }

    // get duration(): TimeInMs {
    //     if (!this.hasLoaded || !this.mediaPlayer.isReady()) return 0;
    //     if (this.adsManager.isPlayingClientSide) return this.adsManager.duration;
    //     if (this.live) return Infinity;

    //     const duration =
    //         (this.mediaPlayer?.getDashMetrics()?.getCurrentDVRInfo('video')?.range?.end !== undefined
    //             && this.mediaPlayer?.getDashMetrics()?.getCurrentDVRInfo('video')?.range?.start !== undefined
    //         ) ?
    //             (this.mediaPlayer.getDashMetrics().getCurrentDVRInfo('video').range.end
    //                 - this.mediaPlayer.getDashMetrics().getCurrentDVRInfo('video').range.start)
    //             * 1000
    //             : 0;

    //     console.log('jan ano tady', duration, this.mediaPlayer.durationAsUTC());
    //     return secondsToMilliseconds(duration) || 0;
    // }

    /**
     * Gets seeking boundaries for current media in ms.
     *
     * @returns {ISeekableRange}
     */
    get seekableRange(): ISeekableRange {
        if (
            !this.hasLoaded ||
            !this.mediaPlayer.isReady() ||
            this.adsManager.isPlayingClientSide ||
            (this.live && this.initialStartPositionInMs === null)
        )
            return { start: 0, end: 0 } as ISeekableRange;

        const durationMs = secondsToMilliseconds(this.mediaPlayer.duration());

        let seekableStart = 0;
        let seekableEnd = durationMs;

        if (this.live) {
            const endMs = secondsToMilliseconds(this.mediaPlayer.durationAsUTC());
            const targetLiveDelay = this.getTargetLiveDelay();
            seekableStart = Math.max(0, endMs - durationMs - this.initialStartPositionInMs);
            seekableEnd = endMs - this.initialStartPositionInMs - targetLiveDelay;
        }

        seekableStart = this.applySeekingRestriction(seekableStart);
        seekableEnd = this.applySeekingRestriction(seekableEnd - 27000); // restriction to prevent from seeking near life edge PRJ1260EXT-10276

        return {
            start: seekableStart,
            end: seekableEnd
        } as ISeekableRange;
    }

    /**
     * Gets live DVR window in ms.
     *
     * @returns {ISeekableRange}
     */
    get timeshiftRange(): ISeekableRange {
        if (
            !this.hasLoaded ||
            !this.mediaPlayer.isReady() ||
            this.adsManager.isPlayingClientSide ||
            !this.live
        )
            return { start: 0, end: 0 } as ISeekableRange;

        const durationMs = secondsToMilliseconds(this.mediaPlayer.duration());


        const endMs = secondsToMilliseconds(this.mediaPlayer.durationAsUTC());
        const targetLiveDelay = this.getTargetLiveDelay();
        const seekableStart = Math.max(
            0,
            endMs - durationMs - this.initialStartPositionInMs
        );
        const seekableEnd =
            endMs - this.initialStartPositionInMs - targetLiveDelay;

        return this.initialStartPositionInMs ? {
            start: seekableStart,
            end: seekableEnd,
        } as ISeekableRange : { start: 0, end: durationMs } as ISeekableRange;
    }

    private getTargetLiveDelay(): number {
        /* index.d.ts still does not include getTargetLiveDelay()
           even though it IS in DASH.js source files */
        return secondsToMilliseconds((this.mediaPlayer as any).getTargetLiveDelay() || 0);
    }

    /**
     * Retrieve the current media's buffered ranges, if possible.
     *
     * @returns {BufferedRanges}
     * @memberof PlayerBase
     */
    get bufferedRanges(): BufferedRanges {
        if (!this.hasLoaded || !this.mediaPlayer.isReady() || this.adsManager.isPlayingClientSide)
            return [{ start: 0, end: 0 }];
        const bufferLength = this.mediaPlayer.getBufferLength(MediaTypes.VIDEO as MediaType);
        if (Number.isNaN(bufferLength)) {
            return [{ start: 0, end: 0 }];
        }
        const start = this.currentTime;
        return [
            {
                start,
                end: start + secondsToMilliseconds(bufferLength)
            }
        ];
    }

    /**
     * Map content time to stream time
     * @param time Time in content
     * @param seekType How to interpret time
     * @returns Time in stream (like currentTime)
     */
    public getStreamTime(time: number, seekType: SeekType = SeekType.CONTENT_TIME): number {
        const { live, seekableRange, initialStartPositionInMs } = this;
        const { end: seekableEnd } = seekableRange;
        if (live && initialStartPositionInMs === null) return NaN;
        let finalTime = time;
        if (seekType === SeekType.CONTENT_TIME) {
            finalTime = this.adsManager.getMediaTime(time);
        }
        if (seekType === SeekType.TIMESHIFT && live) {
            finalTime = seekableEnd + time;
        }
        if (seekType === SeekType.UTC) {
            if (live) {
                finalTime = time - initialStartPositionInMs;
            } else {
                const programStart = this.source?.metadata?.programStart || 0;
                finalTime = time - programStart;
            }
        }
        return finalTime;
    }

    protected mediaSeek(finalTime: TimeInMs): void {
        if (!this.hasLoaded) return;
        const { live, timeshiftRange } = this;
        if (live) {
            this.mediaPlayer.seek(millisecondsToSeconds(finalTime - timeshiftRange.start));
        } else {
            this.mediaPlayer.seek(millisecondsToSeconds(finalTime));
        }
    }

    getUTCTimestamp(value?: number): Date | null {
        if (this.live) {
            if (typeof value !== 'number') return new Date(this.mediaPlayer.timeAsUTC());
            if (this.initialStartPositionInMs !== null)
                return new Date(this.initialStartPositionInMs + value);
        }
        return super.getUTCTimestamp(value);
    }

    /**
     * Retrieve thumbnail by time position
     */
    private readonly promisifiedGetThumbmail = (time: TimeInMs): Promise<dashjs.Thumbnail> =>
        new Promise((resolve) => {
            this.mediaPlayer.provideThumbnail(time, (thumbnail) => {
                resolve(thumbnail);
            });
        });

    /**
     * Retrieve thumbnails
     */
    public async getThumbnails(timesInMs: number[]): Promise<Array<IThumbnail> | null> {
        const items: IThumbnail[] = [];
        for (let i = 0; i < timesInMs.length; i++) {
            // according to documentation, needs to be in seconds and relative to duration range (x / duration)
            const time = (timesInMs[i] - this.seekableRange?.start) / 1000;
            const thumbnail: dashjs.Thumbnail = await this.promisifiedGetThumbmail(3000);
            if (thumbnail) items.push(thumbnail as IThumbnail);
            if (i === timesInMs.length - 1) {
                return Promise.resolve(items);
            }
        }
        // if stream itself has not thumbnails, try as second try to check if thumbnails come from backstage format (html5Video)
        return super.getThumbnails(timesInMs);
    }

    /*
     * TRACKS SELECTION
     */

    /**

     * Gets active audiotrack
     */
    get selectedAudioTrack(): IAudioTrack {
        return this.audioTracks.find((audioTrack) => audioTrack.selected);
    }

    /**

     * Retrieve the current media's text track.
     * TODO: valid integration for HTML5video as base integration?
     *  so no override?
     *
     * @returns {ITextTrack}
     */
    get selectedTextTrack(): ITextTrack {
        return this.textTracks.find((track) => track.selected);
    }

    /**
     * Gets active audiotrack
     */
    get selectedVideoTrack(): IVideoTrack {
        return this.videoTracks.find((videoTrack) => videoTrack.selected);
    }

    /**
     * Gets all audiotracks from the source
     *
     * @returns {Array<IAudioTrack>}
     */
    get audioTracks(): Array<IAudioTrack> {
        if (!this.hasLoaded) return [];
        const activeStream = this.mediaPlayer.getActiveStream();
        if (!activeStream) return [];
        const mediaType = MediaTypes.AUDIO as MediaType;
        const currentAudioTrack = this.mediaPlayer.getCurrentTrackFor(mediaType);
        const audioMediaInfos = this.mediaPlayer.getTracksFor(mediaType);
        if (!audioMediaInfos.length) return [];
        const alreadyExistingLanguages: Array<string> = [];

        const audioTracks: Array<IAudioTrack> = audioMediaInfos.map((audioTrack: MediaInfo) =>
            mapToAudioTrack(audioTrack, currentAudioTrack)
        );

        /*
         * Sometimes there are same tracks main/alternative, witch same language.
         * This way we filter those out.
         */
        return audioTracks.filter((track) => {
            const audioLanguageChannel = `${track.language}${track.channels}`;
            const exists = alreadyExistingLanguages.includes(audioLanguageChannel);
            alreadyExistingLanguages.push(audioLanguageChannel);
            return !exists;
        });
    }

    /**
     * Sets a desired audiotrack as active
     */
    set audioTrack(audioTrack: IAudioTrack | null) {
        if (!this.hasLoaded) return;
        this.wasAudioTrackSelected = Boolean(audioTrack);

        if (audioTrack) {
            const newSettings = {
                streaming: {
                    trackSwitchMode: {
                        video: 'alwaysReplace'
                    }
                }
            };
            this.updateSettings(newSettings);
            this.mediaPlayer.setCurrentTrack(audioTrack?.extra as MediaInfo);
        }
    }

    /**
     * Returns currently used audioTrack. Can be null if automatic selection is applied.
     */
    get audioTrack(): IAudioTrack | null {
        return this.wasAudioTrackSelected ? this.selectedAudioTrack : null;
    }

    /**
     * Retrieve all available subtitle/caption tracks.
     *
     * @returns {Array<ITextTrack>}
     */
    get textTracks(): Array<ITextTrack> {
        if (!this.hasLoaded) return [];
        const activeStream = this.mediaPlayer.getActiveStream();
        if (!activeStream) return [];
        const mediaType = MediaTypes.TEXT as MediaType;
        const isEnabled = this.mediaPlayer.isTextEnabled();
        const currentTextTrack = this.mediaPlayer.getCurrentTrackFor(mediaType);

        if (this.mediaPlayer.getTracksFor(mediaType).length) {
            const textTracks = this.mediaPlayer.getTracksFor(mediaType).map((track: MediaInfo) => {
                if (!track) return null;
                const mappedTrack = mapToTextTrack(track, currentTextTrack);
                return {
                    ...mappedTrack,
                    selected: isEnabled && mappedTrack.selected
                };
            });

            const isEmptyTrackActive = !isEnabled || !currentTextTrack;
            return [this.getEmptyTextTrack(isEmptyTrackActive), ...textTracks];
        }

        return super.getTextTracks();
    }

    /**
     * Set the current media's text track.
     *
     * @param {ITextTrack|null} textTrack - TextTrack to be set.
     */
    set textTrack(textTrack: ITextTrack | null) {
        if (!this.hasLoaded) return;
        const textType = MediaTypes.TEXT as MediaType;
        if (this.mediaPlayer.getTracksFor(textType).length) {
            const shouldTextTracksBeDisplayed = textTrack && textTrack.id !== EMPTY_TRACK_ID;
            this.wasTextTrackSelected = Boolean(textTrack) && shouldTextTracksBeDisplayed;

            this.mediaPlayer.enableText(shouldTextTracksBeDisplayed);

            if (textTrack && shouldTextTracksBeDisplayed) {
                /*
                 * DashJS needs the originally returned indexes in order to select correct subs.
                 * The ID we provide is not enough.
                 */
                const nativeSelectedTrackIndex = this.mediaPlayer
                    .getTracksFor(textType)
                    .findIndex((track: MediaInfo) => textTrack.id === mapToTextTrack(track).id);

                if (nativeSelectedTrackIndex >= 0)
                    this.mediaPlayer.setTextTrack(nativeSelectedTrackIndex);
                else return;
            }
            this.triggerEvent('texttrackchanged', {
                track: this.selectedTextTrack
            });
        } else {
            super.setTextTrack(textTrack);
        }
    }

    /**
     * Returns currently used textTrack. Can be null if automatic selection is applied.
     */
    get textTrack(): ITextTrack | null {
        return this.wasTextTrackSelected ? this.selectedTextTrack : null;
    }

    /**
     * Gets variant tracks (quality)
     */
    get videoTracks(): Array<IVideoTrack> {
        // const { quality } = this.initializationConfig;
        const mediaType = MediaTypes.VIDEO as MediaType;
        const currentIndexQuality = this.mediaPlayer.getQualityFor(mediaType);

        const variantsTracks = this.mediaPlayer.getBitrateInfoListFor(mediaType);

        if (!variantsTracks.length) return [];

        const videoTracks: Array<IVideoTrack> = variantsTracks.map((track: BitrateInfo) => {
            const isInitialQty = true;
            if (isInitialQty) this.isAutomaticModeEnabled = false;

            return {
                ...mapToVideoTrack(track),
                selected:
                    isInitialQty ||
                    (!this.isAutomaticModeEnabled && track.qualityIndex === currentIndexQuality)
            };
        });

        /*
         * Sometimes there are same tracks main/alternative, witch same language.
         * This way we filter those out.
         */
        let videoOptions = videoTracks.filter((track) => {
            const isInRange = true;
            const isHeightDuplicated = videoTracks.find(
                (t) => track !== t && t.height === track.height && track.id < t.id
            );
            return !isHeightDuplicated && isInRange;
        });

        if (videoOptions.length) {
            // sort by definition
            videoOptions = videoOptions.sort((a, b) => b.height - a.height);

            // Add Auto option if the alteratives quality to select exists
            const currentQty = variantsTracks.find(
                (opt: BitrateInfo) => opt.qualityIndex === currentIndexQuality
            );

            const noneSelected = !videoOptions.find((item) => item.selected);
            videoOptions.unshift({
                ...mapToVideoTrack(currentQty),
                id: '-1',
                label: `Auto${noneSelected && currentQty ? ` (${currentQty.height}p)` : ''}`,
                selected: noneSelected
            });
        }

        return videoOptions;
    }

    /**
     * Sets variant track (quality)
     */
    set videoTrack(videoTrack: IVideoTrack) {
        if (!this.hasLoaded) return;
        this.wasVideoTrackSelected = Boolean(videoTrack);

        if (videoTrack) {
            this.isAutomaticModeEnabled = videoTrack.id === '-1';

            const newSettings = {
                streaming: {
                    abr: {
                        autoSwitchBitrate: {
                            video: this.isAutomaticModeEnabled
                        }
                    }
                }
            };

            // this.updateSettings(newSettings);

            if (!this.isAutomaticModeEnabled) {
                const variantsTracks = this.mediaPlayer.getBitrateInfoListFor(
                    MediaTypes.VIDEO as MediaType
                );

                const newVideoTrack = variantsTracks.find(
                    (el: BitrateInfo) => el.qualityIndex === Number(videoTrack.id)
                );

                this.mediaPlayer.setQualityFor(
                    MediaTypes.VIDEO as MediaType,
                    newVideoTrack.qualityIndex,
                    true
                );
            }

            this.triggerEvent('videotrackchanged', {
                track: this.selectedVideoTrack
            });
        }
    }

    /**
     * Returns currently used videoTrack. Can be null if automatic selection is applied.
     */
    get videoTrack(): IVideoTrack | null {
        return this.wasVideoTrackSelected ? this.selectedVideoTrack : null;
    }

    get performanceTweaks(): IPerformanceTweaks {
        return this.performanceTweaksInternal;
    }

    set performanceTweaks(performanceTweaks: IPerformanceTweaks) {
        this.performanceTweaksInternal = performanceTweaks;
        this.applyPerformaceTweaks();
    }

    private applyPerformaceTweaks() {
        if (!this.mediaPlayer) return;
        const { performanceTweaks } = this;
        let liveDelay = NaN;
        if (typeof performanceTweaks?.liveDelay === 'number') {
            liveDelay = millisecondsToSeconds(performanceTweaks.liveDelay);
        }
        let bufferToKeep: number = null;
        if (typeof performanceTweaks?.backwardBuffer === 'number') {
            bufferToKeep = millisecondsToSeconds(performanceTweaks.backwardBuffer);
        }
        let stableBufferTime: number = null;
        if (typeof performanceTweaks?.aheadBuffer === 'number') {
            stableBufferTime = millisecondsToSeconds(performanceTweaks.aheadBuffer);
        }
        // this.mediaPlayer.updateSettings({
        //     streaming: {
        //         delay: {
        //             liveDelay,
        //         },
        //         buffer: {
        //             bufferToKeep,
        //             stableBufferTime,
        //             bufferTimeAtTopQuality: stableBufferTime,
        //         },
        //     },
        // });
    }

    /*
     * STATE GETTERS
     */

    /**
     * Get media stream information
     *
     * @returns {IMediaInfo} Object that contains playback related media information
     */
    public getMediaInfo(): IMediaInfo {
        const mediaInfo = {
            playerVersion: `${DashJsPlayer.playerName} v.${this.getVersion()}`,
            currentTime: 0,
            duration: 0
        };
        if (!this.mediaPlayer || !this.mediaPlayer.getActiveStream) return mediaInfo;
        let activeStream;
        try {
            activeStream = this.mediaPlayer.getActiveStream();
            if (!activeStream) {
                throw new Error('ActiveStream is null');
            }
        } catch (error) {
            // NO-OP, probably calling getActiveStream before is available, or activeStream is null
            return mediaInfo;
        }
        const videoType = MediaTypes.VIDEO as MediaType;
        const audioType = MediaTypes.AUDIO as MediaType;
        const streamInfo = activeStream.getStreamInfo();
        const dashMetrics = this.mediaPlayer.getDashMetrics();
        const dashAdapter: dashjs.DashAdapter = this.mediaPlayer.getDashAdapter();
        if (dashMetrics && streamInfo) {
            const { index } = streamInfo;
            const repSwitch: dashjs.ICurrentRepresentationSwitch =
                dashMetrics.getCurrentRepresentationSwitch(MediaTypes.VIDEO as MediaType);
            const bitrate = dashAdapter.getBandwidthForRepresentation(
                // @ts-ignore: dashjs not updated types
                repSwitch?.to,
                index
            );
            const bufferLevel = dashMetrics.getCurrentBufferLevel(videoType);
            const bandwidth = Math.round(this.mediaPlayer.getAverageThroughput(videoType));
            // @ts-ignore: dashjs not updated types
            const videoAdaptation = dashAdapter.getAdaptationForType(index, videoType, streamInfo);
            // @ts-ignore: dashjs not updated types
            const audioAdaptation = dashAdapter.getAdaptationForType(index, audioType, streamInfo);
            const qualityVideoId = this.mediaPlayer.getQualityFor(videoType);
            const qualityAudioId = this.mediaPlayer.getQualityFor(audioType);
            const videoRepresentation = videoAdaptation.Representation_asArray[qualityVideoId];
            const audioRepresentation = audioAdaptation.Representation_asArray[qualityAudioId];
            const droppedVideoFrames = dashMetrics.getCurrentDroppedFrames()?.droppedFrames;
            const { frameRate } = videoRepresentation;
            return {
                ...mediaInfo,
                bufferLevel,
                videoCodec: videoRepresentation.codecs,
                audioCodec: audioRepresentation.codecs,
                resolution: `${videoRepresentation.width}x${videoRepresentation.height}`,
                framerate: frameRate,
                bitrate,
                estimatedBandwidth: bandwidth * BYTES_IN_KB,
                droppedVideoFrames,
                ...(this.live && {
                    liveLatency: this.mediaPlayer.getCurrentLiveLatency() * MILLISECONDS_IN_SECOND
                }),
                currentTime: this.currentTime,
                duration: this.duration
            };
        }
        return null;
    }

    private updateMetrics() {
        const metrics = this.metricsInternal;
        const dashMetrics = this.mediaPlayer.getDashMetrics();
        const bandwidth = this.mediaPlayer.getAverageThroughput('video');
        metrics.bandwidthEstimate = bandwidth * BYTES_IN_KB;
        metrics.liveLatency =
            secondsToMilliseconds(this.mediaPlayer.getCurrentLiveLatency()) || undefined;
        metrics.droppedFrames = dashMetrics?.getCurrentDroppedFrames()?.droppedFrames ?? 0;
    }

    private static errorCodeMap = new Map(
        Object.entries(MediaPlayer.errors)
            .filter((pair) => typeof pair[1] === 'number')
            .map((pair) => [pair[1] as number, pair[0]])
    );

    static getErrorCodeName(errorCode: number): string {
        return this.errorCodeMap.get(errorCode);
    }
}
